import { useResetPylon } from "@/hooks/use-boot-in-app-chat";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useResetSegment, useSegmentTrack } from "@/hooks/use-segment";
import { getPrincipal, setPrincipal } from "@/principal";
import { defaultRetry, defaultThrowOnError, isNotFoundError, isUnauthorizedError } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { hashKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Account, type JoinableOrganization, type LoginProvider } from "gitpod-next-api/gitpod/v1/account_pb";

export type PlainAccount = PlainMessage<Account>;
const toPlainAccount = (account: Account): PlainAccount => {
    return toPlainMessage(account);
};

export type PlainJoinableOrganization = PlainMessage<JoinableOrganization>;
const toPlainJoinableOrganization = (organization: JoinableOrganization): PlainJoinableOrganization => {
    return toPlainMessage(organization);
};

export type PlainLoginProvider = PlainMessage<LoginProvider>;
const toPlainLoginProvider = (p: LoginProvider): PlainLoginProvider => {
    return toPlainMessage(p);
};

const key = ["account"];
export const getAccountQueryKey = () => key;
export const getJoinableOrganizationsQueryKey = () => ["joinable-organizations"];

export const useGetAccount = () => {
    const client = useQueryClient();
    const api = useGitpodAPI();

    return useQuery({
        queryKey: getAccountQueryKey(),
        queryFn: async () => {
            let account: PlainAccount;
            try {
                const { account: rawAccount } = await api.accountService.getAccount({});
                if (!rawAccount) {
                    throw new Error("Account not found");
                }
                account = toPlainAccount(rawAccount);
            } catch (error) {
                // Clear the account for unauthenticated errors
                if (isUnauthorizedError(error)) {
                    // Remove queries to purge any data since account is not authenticated
                    client.removeQueries({
                        type: "all",
                        // all keys except the getAccountQueryKey query - otherwise we'll be stuck in a loading state forever
                        predicate: (query) => {
                            const removeQuery = hashKey(query.queryKey) !== hashKey(getAccountQueryKey());
                            return removeQuery;
                        },
                    });
                    return null;
                }

                // Clear the session for not found errors
                // This happens when an account is deleted (or the db has been wiped) while they are logged in
                if (isNotFoundError(error)) {
                    await fetch("/auth/oidc/logout", {
                        method: "POST",
                        credentials: "same-origin",
                    });

                    return null;
                }

                throw error;
            }

            if (account) {
                const memberships = account.memberships || [];

                // if we have a principal, check if it's still valid
                const currentPrincipal = getPrincipal();
                if (currentPrincipal) {
                    if (memberships.some((m) => m.userId === currentPrincipal)) {
                        // previous principal is still valid
                        return account;
                    } else {
                        // previous principal is not valid anymore
                        setPrincipal("");
                    }
                }

                // set principal to first membership
                const membership = memberships[0];
                const userId = membership?.userId;
                if (userId) {
                    setPrincipal(userId);
                }

                return account;
            }
        },
        throwOnError: defaultThrowOnError,
        retry(failureCount, error) {
            // Don't retry if it's an unauthenticated error
            if (isUnauthorizedError(error)) {
                return false;
            }

            return defaultRetry(failureCount, error);
        },
        // Refetch the account to regularly check if session is still active/valid
        refetchInterval(query) {
            // Don't refetch if we're unauthenticated
            if (isUnauthorizedError(query.state.error) || query.state.data === null) {
                return false;
            }

            // otherwise refetch every minute
            return 1000 * 60;
        },
        staleTime: 1000 * 50,
        refetchOnMount: false, // disable additional instances of a query to trigger background refetches
    });
};

export const useJoinableOrganizations = () => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: getJoinableOrganizationsQueryKey(),
        queryFn: async () => {
            const joinableOrganizations = await autoPaginate(
                (params) => api.accountService.listJoinableOrganizations(params),
                {},
                (response) => response.joinableOrganizations,
            );
            if (!joinableOrganizations) {
                throw new Error("Joinable organizations not found");
            }
            return { joinableOrganizations: joinableOrganizations.map(toPlainJoinableOrganization) };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useListLoginProviders = (inviteId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: ["loginProviders", { inviteId }],
        queryFn: async () => {
            let allowCustom = false;
            const loginProviders = await autoPaginate(
                (params) => api.accountService.listLoginProviders(params),
                {
                    filter: inviteId ? { inviteId } : {},
                },
                (response) => {
                    // Capture allowCustom from the first response
                    allowCustom = response.allowCustom;
                    return response.loginProviders;
                },
            );

            if (!loginProviders) {
                throw new Error("Login providers not found");
            }

            return {
                providers: loginProviders.map(toPlainLoginProvider),
                allowCustom,
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 60 * 60 * 1000, // 1 hour
    });
};

export const useDeleteAccount = () => {
    const api = useGitpodAPI();
    const segmentTrack = useSegmentTrack();
    const client = useQueryClient();
    const resetPylon = useResetPylon();
    const resetMixpanel = useResetSegment();

    return useMutation({
        mutationFn: async ({ accountId }: { accountId: string }) => {
            const response = await api.accountService.deleteAccount({ accountId });
            return response;
        },
        onSettled: (_response, error, variables) => {
            if (!variables.accountId) {
                return;
            }
            if (error) {
                segmentTrack("Account delete failed", {
                    accountId: variables.accountId,
                    error: error.message || "Unknown error",
                });
            } else {
                segmentTrack("Account delete succeeded", {
                    accountId: variables.accountId,
                });
                client.clear();
                resetPylon();
                resetMixpanel();
            }
        },
    });
};
